/* eslint-disable no-nested-ternary */
import { useState, useCallback, Dispatch, SetStateAction, FC } from 'react';
import { useQuery } from 'react-apollo';
import amplitude from 'amplitude-js';
import { useCurrentWidth } from 'new_shared/useWIndowDimensions';

import { useAppDispatch } from 'hooks/useAppStore';
import {
	authenticate,
	IUserClaimsAuth,
	signInAdmin,
	signInGoogleEstudiante,
	signIngoogleUserCuenta,
	signInMicrosoftEstudiante,
	signInMicrosoftUserCuenta
} from 'redux/reducersV2/authAction';
import { selectCurrentUser } from 'redux/selectors/auth';
import { useSelector } from 'react-redux';
import { logout } from 'redux/reducersV2/authSlice';
import { Users, Users_Cuentas } from 'generated/graphql';
import { TipoUsuarioEnum } from 'enums/tipoUsuario';
import LeftPanel from './LeftPanel';
import RightPanel from './RightPanel';
import { signInPasswordEstudiante, signInPasswordUserCuenta } from './utils';
import {
	GET_ESTUDIANTE_BY_SWARMOB_DATA,
	GET_USER_ACCOUNT_BY_CREDENTIALS_DOCENTE,
	GET_USER_ACCOUNT_BY_CREDENTIALS_MONITOR
} from './graphql';
import AccountSelectionModal, { IUserItem } from './AccountSelectionModal';

const useImperativeQuery = (query: any) => {
	const { refetch } = useQuery(query, { skip: true });

	const imperativelyCallQuery = (variables: any) => {
		return refetch(variables);
	};

	return imperativelyCallQuery;
};

const setHandleHasError = (fn: Dispatch<SetStateAction<boolean>>) => {
	fn(() => true);
	setTimeout(() => fn(() => false), 3000);
};

const formatAccountData = (authData: IUserClaimsAuth) => {
	if (!authData || !authData.l) return null;

	const accounts: {
		profesor?: Array<{ hasuraKey: string }>;
		monitor?: Array<{ hasuraKey: string }>;
		coordinador?: Array<{ hasuraKey: string }>;
		user?: { hasuraKey: string };
	} = {};

	// Format profesor accounts (multiple possible)
	if (authData.l.p && authData.l.p.length > 0) {
		accounts.profesor = authData.l.p.map((profesorData: string) => ({
			hasuraKey: profesorData
		}));
	}

	// Format monitor accounts (multiple possible)
	if (authData.l.m && authData.l.m.length > 0) {
		accounts.monitor = authData.l.m.map((monitorData: string) => ({
			hasuraKey: monitorData
		}));
	}
	if (authData.l.c && authData.l.c.length > 0) {
		accounts.coordinador = authData.l.c.map((coordinadorData: string) => ({
			hasuraKey: coordinadorData
		}));
	}

	// Format user account (single)
	if (authData.l.u) {
		accounts.user = {
			hasuraKey: authData.l.u
		};
	}

	return accounts;
};

// Add this function before the LoginRedesign component
const formatUserAccountsForSelection = (cuentas: Users_Cuentas[], userType: TipoUsuarioEnum) => {
	// Create an empty result object with the correct structure
	const accounts: {
		profesor?: Array<{ hasuraKey: string }>;
		monitor?: Array<{ hasuraKey: string }>;
		user?: { hasuraKey: string };
	} = {};

	if (userType === TipoUsuarioEnum.monitor) {
		accounts.monitor = cuentas.map((cuenta) => ({
			hasuraKey: cuenta?.key
		}));
	} else if (userType === TipoUsuarioEnum.profesor) {
		accounts.profesor = cuentas.map((cuenta) => ({
			hasuraKey: cuenta?.key
		}));
	} else if (userType === TipoUsuarioEnum.coordinador) {
		accounts.profesor = cuentas.map((cuenta) => ({
			hasuraKey: cuenta?.key
		}));
	}
	accounts.user = {
		hasuraKey: cuentas[0]?.key // Assuming the first account is the user account
	};

	return accounts;
};

const LoginRedesign: FC = () => {
	const [currentUser, setCurrentUser] = useState<TipoUsuarioEnum>(TipoUsuarioEnum.noUser);
	const [hasError, setHasError] = useState(false);
	const [noActive, setNoActive] = useState(false);
	const dispatch = useAppDispatch();
	const [passwordValue, setPasswordValue] = useState<string>('');

	const authData = useSelector(selectCurrentUser) as unknown as IUserClaimsAuth;

	const [userRef, setUserRef] = useState<{
		cursoKey: string;
		nombre: string;
		apellido: string;
		contraseña: string;
		colegioKey: string;
		institucionKey: string;
	}>({
		cursoKey: '',
		nombre: '',
		apellido: '',
		contraseña: '',
		colegioKey: '',
		institucionKey: ''
	});
	const [customLoader, setCustomLoader] = useState<boolean>(false);
	const [passwordSelectionVisible, setPasswordSelectionVisible] = useState(false);
	const [passwordLoginAccounts, setPasswordLoginAccounts] = useState<any>(null);
	const [userKey, setUserKey] = useState<string | null>(null);
	const [visible, setVisible] = useState(false);
	const { mobileWidth } = useCurrentWidth();

	const estudianteQuery = useImperativeQuery(GET_ESTUDIANTE_BY_SWARMOB_DATA);
	const userQueryMonitor = useImperativeQuery(GET_USER_ACCOUNT_BY_CREDENTIALS_MONITOR);
	const userQueryDocente = useImperativeQuery(GET_USER_ACCOUNT_BY_CREDENTIALS_DOCENTE);

	const onChangeUser = useCallback((tipo: TipoUsuarioEnum) => setCurrentUser(tipo), []);

	const onSubmitPassword = useCallback(async () => {
		if (currentUser === TipoUsuarioEnum.estudiante) {
			if (
				userRef.cursoKey === '' ||
				userRef.nombre === '' ||
				userRef.apellido === '' ||
				userRef.contraseña === ''
			) {
				console.log('no data student');
				return setHandleHasError(setHasError);
			}
		}

		if (currentUser === TipoUsuarioEnum.profesor) {
			if (userRef.nombre === '' || userRef.apellido === '' || userRef.contraseña === '') {
				console.log('no data profesor');
				return setHandleHasError(setHasError);
			}
		}

		const response =
			currentUser === TipoUsuarioEnum.estudiante
				? await estudianteQuery({
						curso: userRef.cursoKey,
						password: userRef.contraseña.trim(),
						nombre: userRef.nombre.trim()?.split(' ')?.[0], // Usar comodines para buscar similitudes
						apellido: userRef.apellido.trim()?.split(' ')?.[0] ?? ''
				  })
				: currentUser === TipoUsuarioEnum.monitor
				? await userQueryMonitor({
						institucion_key: userRef.institucionKey ? userRef.institucionKey : null,
						password: userRef.contraseña.trim(),
						nombre: userRef.nombre.trim()?.split(' ')?.[0], // Usar comodines para buscar similitudes
						apellido: userRef.apellido.trim()?.split(' ')?.[0] ?? '',
						tipoUsuario: currentUser
				  })
				: await userQueryDocente({
						colegio_key: userRef.colegioKey ? userRef.colegioKey : null,
						password: userRef.contraseña.trim(),
						nombre: userRef.nombre.trim()?.split(' ')?.[0], // Usar comodines para buscar similitudes
						apellido: userRef.apellido.trim()?.split(' ')?.[0] ?? '',
						tipoUsuario: currentUser
				  });

		if (
			response?.data?.buscar_estudiantes_por_curso_password?.length > 0 &&
			currentUser === TipoUsuarioEnum.estudiante
		) {
			if (response.data.buscar_estudiantes_por_curso_password[0].active === false) {
				return setHandleHasError(setNoActive);
			}
			amplitude.getInstance().logEvent('[ESTUDANTE] Login password escrita', {
				user_key: response.data.buscar_estudiantes_por_curso_password[0].key,
				nombre: response.data.buscar_estudiantes_por_curso_password[0].nombre
			});
			localStorage.setItem('passLogin', 'true');
			return signInPasswordEstudiante(
				response.data.buscar_estudiantes_por_curso_password,
				setCustomLoader,
				dispatch,
				() => setHandleHasError(setHasError)
			);
		}

		// docentes y monitores podran estar con este boton
		if (response?.data?.buscar_usuario_por_credencialesv2?.length > 0 && currentUser === TipoUsuarioEnum.profesor) {
			const dataProfesor = response?.data?.buscar_usuario_por_credencialesv2?.[0] as Users;
			const cuentas = dataProfesor.user_cuentas?.filter((uc) => uc.tipoUsuario === TipoUsuarioEnum.profesor);
			console.log('log redesin pass prof ');
			if (cuentas.length === 1) {
				const cuentaElegida = cuentas?.[0];
				if (cuentaElegida.active === false) {
					return setHandleHasError(setNoActive);
				}
				amplitude.getInstance().logEvent('[PROFESOR] Login password escrita', {
					user_key: cuentaElegida.key,
					nombre: dataProfesor.nombre
				});
				console.log('cuenta elegida', cuentaElegida);
				return signInPasswordUserCuenta(
					[
						{
							firebase_uid: cuentaElegida.firebase_uid,
							key: cuentaElegida.key,
							nombre: dataProfesor.nombre,
							colegio: cuentaElegida.colegio_key,
							avatarPath: dataProfesor.avatarPath,
							rol: cuentaElegida.rol,
							email: cuentaElegida.email
						} as any
					],
					setCustomLoader,
					dispatch,
					() => setHandleHasError(setHasError),
					currentUser
				);
			}
			if (cuentas.length > 1) {
				console.log('varios users, elegir mon', cuentas);
				// Format accounts for selection modal
				const passwordLoginAccounts = formatUserAccountsForSelection(cuentas, currentUser);
				// Store this data temporarily to use later
				setPasswordLoginAccounts(passwordLoginAccounts);
				// Show the selection modal
				setPasswordSelectionVisible(true);
				setUserKey(cuentas[0].key);
			}
			if (cuentas.length === 0) {
				console.log('no hay cuentas');
				setHandleHasError(setNoActive);
			}
		}

		if (
			response?.data?.buscar_usuario_por_credencialesv2?.length > 0 &&
			currentUser === TipoUsuarioEnum.coordinador
		) {
			const dataProfesor = response.data.buscar_usuario_por_credencialesv2?.[0] as Users;
			const cuentas = dataProfesor.user_cuentas?.filter((uc) => uc.tipoUsuario === TipoUsuarioEnum.coordinador);
			console.log('log redesin pass coordinador ');

			if (cuentas.length === 1) {
				const cuentaElegida = cuentas?.[0];
				if (cuentaElegida.active === false) {
					return setHandleHasError(setNoActive);
				}
				amplitude.getInstance().logEvent('[COORDINADOR] Login password escrita', {
					user_key: cuentaElegida.key,
					nombre: dataProfesor.nombre
				});
				return signInPasswordUserCuenta(
					[
						{
							firebase_uid: cuentaElegida.firebase_uid,
							key: cuentaElegida.key,
							nombre: dataProfesor.nombre,
							colegio: cuentaElegida.colegio_key,
							avatarPath: dataProfesor.avatarPath,
							rol: 'coordinador',
							email: cuentaElegida.email
						} as any
					],
					setCustomLoader,
					dispatch,
					() => setHandleHasError(setHasError),
					currentUser
				);
			}
			if (cuentas.length > 1) {
				console.log('varios users, elegir mon', cuentas);
				// Format accounts for selection modal
				setUserKey(cuentas[0].key);
				const passwordLoginAccounts = formatUserAccountsForSelection(cuentas, currentUser);
				// Store this data temporarily to use later
				setPasswordLoginAccounts(passwordLoginAccounts);
				// Show the selection modal
				setPasswordSelectionVisible(true);
			}
			if (cuentas.length === 0) {
				console.log('no hay cuentas');
				setHandleHasError(setNoActive);
			}
		}

		if (response?.data?.buscar_usuario_por_credencialesv2?.length > 0 && currentUser === TipoUsuarioEnum.monitor) {
			const dataProfesor = response.data.buscar_usuario_por_credencialesv2[0] as Users;
			const cuentas = dataProfesor.user_cuentas?.filter((uc) => uc.tipoUsuario === TipoUsuarioEnum.monitor);
			console.log('log redesin pass monitor ');

			if (cuentas.length === 1) {
				const cuentaElegida = cuentas?.[0];
				if (cuentaElegida.active === false) {
					return setHandleHasError(setNoActive);
				}
				amplitude.getInstance().logEvent('[MONITOR] Login password escrita', {
					user_key: cuentaElegida.key,
					nombre: dataProfesor.nombre
				});
				return signInPasswordUserCuenta(
					[
						{
							firebase_uid: cuentaElegida.firebase_uid,
							key: cuentaElegida.key,
							nombre: dataProfesor.nombre,
							institucion: cuentaElegida.institucion_key,
							avatarPath: dataProfesor.avatarPath,
							rol: cuentaElegida.rol,
							email: cuentaElegida.email
						} as any
					],
					setCustomLoader,
					dispatch,
					() => setHandleHasError(setHasError),
					currentUser
				);
			}
			if (cuentas.length > 1) {
				console.log('varios users, elegir mon', cuentas);
				// Format accounts for selection modal
				const passwordLoginAccounts = formatUserAccountsForSelection(cuentas, currentUser);
				// Store this data temporarily to use later
				setPasswordLoginAccounts(passwordLoginAccounts);
				// Show the selection modal
				setPasswordSelectionVisible(true);
				setUserKey(cuentas[0].key);
			}
			if (cuentas.length === 0) {
				setHandleHasError(setNoActive);
			}
		}

		if (
			response?.data?.buscar_usuario_por_credencialesv2?.length === 0 ||
			response?.data?.buscar_estudiantes_por_curso_password?.length === 0
		) {
			setHandleHasError(setHasError);
		}
	}, [currentUser, dispatch, estudianteQuery, userQueryDocente, userQueryMonitor, userRef]);

	const onAdminSignIn = useCallback(() => {
		dispatch(signInAdmin() as any);
	}, [dispatch]);

	const onGoogleSignIn = useCallback(() => {
		if (currentUser === TipoUsuarioEnum.estudiante) {
			dispatch(signInGoogleEstudiante());
		} else if (
			currentUser === TipoUsuarioEnum.profesor ||
			currentUser === TipoUsuarioEnum.coordinador ||
			currentUser === TipoUsuarioEnum.monitor
		) {
			dispatch(signIngoogleUserCuenta(setVisible, currentUser));
		}
	}, [currentUser, dispatch]);

	const onMicrosoftSignIn = useCallback(() => {
		if (currentUser === TipoUsuarioEnum.estudiante) {
			dispatch(signInMicrosoftEstudiante() as any);
		} else if (
			currentUser === TipoUsuarioEnum.profesor ||
			currentUser === TipoUsuarioEnum.coordinador ||
			currentUser === TipoUsuarioEnum.monitor
		) {
			dispatch(signInMicrosoftUserCuenta(setVisible, currentUser));
		}
	}, [currentUser, dispatch]);

	const onAccountSelect = useCallback(
		(accountType: string, account: IUserItem, loginWithPassword?: boolean) => {
			if (loginWithPassword) {
				amplitude.getInstance().logEvent(`[${accountType}] Login password escrita `, {
					user_key: account.hasuraKey,
					nombre: account.nombre
				});

				return signInPasswordUserCuenta(
					[
						{
							firebase_uid: account.firebase_uid,
							key: account.hasuraKey,
							nombre: account.nombre,
							colegio: account.colegioKey,
							institucion: account.institucionKey,
							avatarPath: account.avatarPath,
							rol: account.rol,
							email: account.email
						} as any
					],
					setCustomLoader,
					dispatch,
					() => setHandleHasError(setHasError),
					currentUser
				);
			}

			console.log('onAccountSelect', accountType, account);

			localStorage.setItem('key', account.hasuraKey);
			localStorage.setItem('tp', account.tipoUsuario);
			dispatch(authenticate(account));
		},
		[currentUser, dispatch]
	);

	const accountData = formatAccountData(authData);

	const handleCancel = useCallback(() => {
		setVisible(false);
		dispatch(logout);
		setUserRef({
			cursoKey: '',
			nombre: '',
			apellido: '',
			contraseña: '',
			colegioKey: '',
			institucionKey: ''
		});
	}, [dispatch]);

	return (
		<div className={mobileWidth ? 'p-2' : 'grid grid-cols-2 h-screen'}>
			{authData.a && authData.l && accountData && currentUser && (
				<AccountSelectionModal
					visible={visible}
					onAccountSelect={onAccountSelect}
					accounts={accountData}
					onCancel={handleCancel}
					currentUser={currentUser}
				/>
			)}
			{passwordLoginAccounts && currentUser && userKey && (
				<AccountSelectionModal
					visible={passwordSelectionVisible}
					onAccountSelect={(accountType, account) => onAccountSelect(accountType, account, true)}
					accounts={passwordLoginAccounts}
					onCancel={() => {
						setPasswordSelectionVisible(false);
						setPasswordLoginAccounts(null);
					}}
					currentUser={currentUser}
					userKey={userKey}
				/>
			)}
			<LeftPanel
				onSubmitPassword={onSubmitPassword}
				setPasswordValue={setPasswordValue}
				onChangeUser={onChangeUser}
				currentUser={currentUser}
				onAdminSignIn={onAdminSignIn}
				onGoogleSignIn={onGoogleSignIn}
				onMicrosoftSignIn={onMicrosoftSignIn}
				hasError={hasError}
				noActive={noActive}
				customLoader={customLoader}
				setUserRef={setUserRef}
				userRef={userRef}
			/>
			{!mobileWidth && <RightPanel />}
		</div>
	);
};

export default LoginRedesign;
